import React, { createContext, FC, useEffect, useState, useContext } from "react";
import { cloneDeep } from "lodash";
import { toast } from "react-toastify";
import { BenchmarkingService } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import {
  BenchmarkingCounty,
  BenchmarkingLineOfBusinessEnum,
  BenchmarkingNetwork,
  BenchmarkingState,
} from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import {
  MedicareMembershipCountTypeEnum,
  MedicareNetwork,
  MedicareParentOrganization,
  MedicareParentOrganizationSortingTypeEnum,
  MedicarePrimaryFiltersPayload,
  MedicareStarRatingTypeEnum,
} from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { UserContext } from "@app/user";
import { BenchmarkingPlanTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingPlanTypeEnum";
import { BenchmarkingDashboardSnpPlanFilterEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardSnpPlanFilterEnum";

export interface MedicareFiltersContextValues {
  applyingServiceArea: MedicareNetwork;
  counties: DropdownItem<BenchmarkingCounty>[];
  mappedPlanTypes: DropdownItem<BenchmarkingPlanTypeEnum>[];
  mappedStarRatings: DropdownItem<MedicareStarRatingTypeEnum>[];
  networkFiltersLoading: boolean;
  onApplyPrimaryFiltersClick: () => void;
  onApplyServiceAreaClick: (countyIds: number[], contract: BenchmarkingNetwork) => void;
  onClearNetworkFiltersClick: () => void;
  onClearPrimaryFiltersClick: () => void;
  parentOrganizations: MedicareParentOrganization[]
  primaryFiltersLoading: boolean;
  selectedCounties: DropdownItem<BenchmarkingCounty>[];
  selectedMembershipCount: MedicareMembershipCountTypeEnum;
  selectedParentOrganizations: MedicareParentOrganization[];
  selectedSortType: MedicareParentOrganizationSortingTypeEnum;
  selectedPlanTypes: DropdownItem<BenchmarkingPlanTypeEnum>[];
  selectedStarRatings: DropdownItem<MedicareStarRatingTypeEnum>[];
  selectedState: BenchmarkingState;
  selectedSnpFilter: BenchmarkingDashboardSnpPlanFilterEnum;
  setSelectedCounties: (selectedCounties: DropdownItem<BenchmarkingCounty>[]) => void;
  setSelectedMembershipCount: (selectedMembershipCount: MedicareMembershipCountTypeEnum) => void;
  setSelectedParentOrganizations: (selectedParentOrganizations: MedicareParentOrganization[]) => void;
  setSelectedSortType: (selectedSortType: MedicareParentOrganizationSortingTypeEnum) => void;
  setSelectedPlanTypes: (selectedPlanTypes: DropdownItem<BenchmarkingPlanTypeEnum>[]) => void;
  setSelectedStarRatings: (selectedStarRatings: DropdownItem<MedicareStarRatingTypeEnum>[]) => void;
  setSelectedState: (selectedState: BenchmarkingState) => void;
  setSelectedSnpFilter: (selectedSnpFilter: BenchmarkingDashboardSnpPlanFilterEnum) => void;
}

const initialValues: MedicareFiltersContextValues = {
  applyingServiceArea: null,
  counties: [],
  mappedPlanTypes: [],
  mappedStarRatings: [],
  networkFiltersLoading: false,
  onApplyPrimaryFiltersClick: () => null,
  onApplyServiceAreaClick: () => null,
  onClearNetworkFiltersClick: () => null,
  onClearPrimaryFiltersClick: () => null,
  parentOrganizations: [],
  primaryFiltersLoading: false,
  selectedCounties: [],
  selectedMembershipCount: null,
  selectedParentOrganizations: [],
  selectedPlanTypes: [],
  selectedSnpFilter: null,
  selectedSortType: null,
  selectedStarRatings: [],
  selectedState: null,
  setSelectedCounties: () => null,
  setSelectedMembershipCount: () => null,
  setSelectedParentOrganizations: () => null,
  setSelectedPlanTypes: () => null,
  setSelectedSnpFilter: () => null,
  setSelectedSortType: () => null,
  setSelectedStarRatings: () => null,
  setSelectedState: () => null,
}

export const MedicareFiltersContext = createContext(initialValues);

const MedicareFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [applyingServiceArea, setApplyingServiceArea] = useState<MedicareNetwork>(null);
  const [counties, setCounties] = useState<DropdownItem<BenchmarkingCounty>[]>(initialValues.counties);
  const [mappedPlanTypes, setMappedPlanTypes] = useState<DropdownItem<BenchmarkingPlanTypeEnum>[]>(initialValues.mappedPlanTypes);
  const [mappedStarRatings, setMappedStarRatings] = useState<DropdownItem<MedicareStarRatingTypeEnum>[]>(initialValues.mappedStarRatings);
  const [networkFiltersLoading, setNetworkFiltersLoading] = useState<boolean>(initialValues.networkFiltersLoading);
  const [parentOrganizations, setParentOrganizations] = useState<MedicareParentOrganization[]>(initialValues.parentOrganizations);
  const [primaryFiltersLoading, setPrimaryFiltersLoading] = useState<boolean>(initialValues.primaryFiltersLoading);
  const [selectedCounties, setSelectedCounties] = useState<DropdownItem<BenchmarkingCounty>[]>(initialValues.selectedCounties);
  const [selectedMembershipCount, setSelectedMembershipCount] = useState<MedicareMembershipCountTypeEnum>(initialValues.selectedMembershipCount);
  const [selectedParentOrganizations, setSelectedParentOrganizations] = useState<MedicareParentOrganization[]>([]);
  const [selectedPlanTypes, setSelectedPlanTypes] = useState<DropdownItem<BenchmarkingPlanTypeEnum>[]>(initialValues.selectedPlanTypes);
  const [selectedSortType, setSelectedSortType] = useState<MedicareParentOrganizationSortingTypeEnum>(null);
  const [selectedStarRatings, setSelectedStarRatings] = useState<DropdownItem<MedicareStarRatingTypeEnum>[]>(initialValues.selectedStarRatings);
  const [selectedState, setSelectedState] = useState<BenchmarkingState>(initialValues.selectedState);
  const [selectedSnpFilter, setSelectedSnpFilter] = useState<BenchmarkingDashboardSnpPlanFilterEnum>(initialValues.selectedSnpFilter);

  const userContext = useContext(UserContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  const benchmarkingService = new BenchmarkingService();

  const starRatings: DropdownItem<MedicareStarRatingTypeEnum>[] = Object.values(MedicareStarRatingTypeEnum).map(x => {
    return { key: x, name: x }
  });

  const planTypes: DropdownItem<BenchmarkingPlanTypeEnum>[] = Object.values(BenchmarkingPlanTypeEnum).map(x => {
    return { key: x, name: x }
  });

  useEffect(() => {
    if (benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicare) {
      setPrimaryFiltersLoading(true);
      benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);

      benchmarkingService.getBenchmarkingPrimaryFilterData(userContext.clientId).then((data) => {
        if (!!data) {
          benchmarkingFiltersContext.setSpecialties(data.specialties);
          benchmarkingFiltersContext.setStates(data.states);
          setSelectedState(null);
          setCounties([]);
          setSelectedCounties([]);
          setSelectedMembershipCount(MedicareMembershipCountTypeEnum.All);
          setMappedStarRatings(cloneDeep(starRatings));
          setSelectedStarRatings(cloneDeep(starRatings));
          setMappedPlanTypes(cloneDeep(planTypes));
          setSelectedPlanTypes(cloneDeep(planTypes));
          setSelectedSnpFilter(BenchmarkingDashboardSnpPlanFilterEnum.All);
        }

        setPrimaryFiltersLoading(false);
      });
    }
  }, [benchmarkingFiltersContext.selectedLineOfBusiness]);

  useEffect(() => {
    if (applyingServiceArea) {
      onApplyPrimaryFiltersClick();
    } else {
      if (!!benchmarkingFiltersContext.displayedPrimaryFilters || !!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
        // on state or county change, clear all displayed filters and selections for both filters
        benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
        onClearNetworkFiltersClick();
      }
    }
  }, [selectedState, selectedCounties, selectedMembershipCount, selectedStarRatings, selectedPlanTypes, selectedSnpFilter]);

  useEffect(() => {
    if (!!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
      // on network change, clear all displayed network filters
      benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
    }
  }, [benchmarkingFiltersContext.selectedNetworks, selectedParentOrganizations]);

  useEffect(() => {
    if (!!selectedState) {
      const mappedCounties: DropdownItem<BenchmarkingCounty>[] = selectedState.counties.map(x => {
        return { key: x, name: x.countyName }
      });

      setCounties(mappedCounties)
      setSelectedCounties(mappedCounties);
    }
  }, [selectedState]);

  const onApplyServiceAreaClick = (countyIds: number[], contract: BenchmarkingNetwork): void => {
    const newSelectedCounties: DropdownItem<BenchmarkingCounty>[] = counties.filter(x => countyIds.includes(x.key.countyId));
    setSelectedCounties(newSelectedCounties)
    setApplyingServiceArea(contract as MedicareNetwork);
  }

  const onApplyPrimaryFiltersClick = (): void => {
    setNetworkFiltersLoading(true);

    const primaryFilters: MedicarePrimaryFiltersPayload = {
      countyIds: selectedCounties.map(x => x.key.countyId),
      lineOfBusinessId: benchmarkingFiltersContext.selectedLineOfBusiness,
      membershipCount: selectedMembershipCount,
      planTypes: selectedPlanTypes.map(x => x.key),
      snpFilter: selectedSnpFilter,
      starRatings: selectedStarRatings.map(x => x.key),
    };

    const filterComparisonNetworks = (parentOrganizations: MedicareParentOrganization[]): MedicareParentOrganization[] => {
      // membership count and star rating filters will only be applied to the competiter networks, not your qes networks
      const filteredParentOrganizations: MedicareParentOrganization[] = [];

      for (const parentOrganization of parentOrganizations) {
        // only apply a membership count filter if all isn't the selected membership count
        if (primaryFilters.membershipCount !== MedicareMembershipCountTypeEnum.All) {
          parentOrganization.hContracts = parentOrganization.hContracts.filter(x => x.membership >= parseInt(primaryFilters.membershipCount.replace(/,/g, "")));
        }

        if (primaryFilters.snpFilter !== BenchmarkingDashboardSnpPlanFilterEnum.All) {
          const snpFilter = primaryFilters.snpFilter === BenchmarkingDashboardSnpPlanFilterEnum.Yes ? true : false;
          parentOrganization.hContracts = parentOrganization.hContracts.filter(x => x.isSnpPlan === snpFilter);
        }

        // only apply a star rating filter if all of the star rating options aren't selected
        if (primaryFilters.starRatings.length !== mappedStarRatings.length) {
          parentOrganization.hContracts = parentOrganization.hContracts.filter(
            x => primaryFilters.starRatings.includes((!!x.rating ? x.rating.toString() : "N/A") as MedicareStarRatingTypeEnum)
          );
        }

        // only apply a plan type filter if all of the plan type options aren't selected
        if (primaryFilters.planTypes.length !== mappedPlanTypes.length) {
          parentOrganization.hContracts = parentOrganization.hContracts.filter(
            x => primaryFilters.planTypes.includes((!!getPlanTypeShortenedName(x.planType) ? getPlanTypeShortenedName(x.planType.toString()) : "N/A") as BenchmarkingPlanTypeEnum)
          );
        }

        // do not display any parent organization that no longer has h contracts visible due to the newly applied filters
        if (!!parentOrganization.hContracts.length) {
          filteredParentOrganizations.push(parentOrganization);
        }
      }

      return filteredParentOrganizations;
    }

    benchmarkingFiltersContext.setDisplayedPrimaryFilters(primaryFilters);

    benchmarkingService.getBenchmarkingNetworkData(primaryFilters).then((data) => {
      if (!!data) {
        const yourNetworks: MedicareParentOrganization = {
          hContracts: data.yourNetworks,
          parentOrganizationName: "Your QES Networks",
        };

        for (const parentOrg of data.comparisonNetworks) {
          for (const contract of parentOrg.hContracts) {
            contract.parentOrganization = parentOrg.parentOrganizationName;
          }
        }

        let newParentOrganizations = [yourNetworks];
        newParentOrganizations = newParentOrganizations.concat(filterComparisonNetworks(data.comparisonNetworks));

        for (const parentOrg of newParentOrganizations) {
          for (const contract of parentOrg.hContracts) {
            contract.parentOrganization = parentOrg.parentOrganizationName;
          }
        }

        setParentOrganizations(newParentOrganizations);

        const newSelectedParentOrganizations: MedicareParentOrganization[] = [cloneDeep(yourNetworks)];

        if (applyingServiceArea) {
          toast("Service Area Updated", {
            containerId: "z-index-9999",
            type: "success",
          });

          if (applyingServiceArea.disabled) {
            benchmarkingFiltersContext.setSelectedNetworks([]);
          } else {
            // when applying the service area, select the parent organization tile of the contract the service area was applied for if the contract is not disabled
            const parentOrganization: MedicareParentOrganization = newParentOrganizations.find(x => x.parentOrganizationName === applyingServiceArea.parentOrganization);

            if (parentOrganization.parentOrganizationName !== yourNetworks.parentOrganizationName) {
              newSelectedParentOrganizations.push(parentOrganization);
            }

            benchmarkingFiltersContext.setSelectedNetworks([applyingServiceArea]);
          }

          setApplyingServiceArea(null);
        }

        setSelectedParentOrganizations(newSelectedParentOrganizations);
      }

      setNetworkFiltersLoading(false);
    });
  }

  const onClearPrimaryFiltersClick = (): void => {
    setSelectedCounties([]);
    setCounties([]);
    setSelectedState(null);
    benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
    setSelectedMembershipCount(MedicareMembershipCountTypeEnum.All);
    setSelectedStarRatings(cloneDeep(starRatings));
    setSelectedPlanTypes(cloneDeep(planTypes));
    setSelectedSnpFilter(BenchmarkingDashboardSnpPlanFilterEnum.All);

    // if resetting the primary filters,
    // also reset the network filters that are based off of the primary filters
    onClearNetworkFiltersClick();
  }

  const onClearNetworkFiltersClick = (): void => {
    setSelectedParentOrganizations([]);
    benchmarkingFiltersContext.setSelectedNetworks([]);
    benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
  }

  const getPlanTypeShortenedName = (planType: string): string => {
    if (!planType) {
      return "";
    } else if (planType.toUpperCase().includes("MEDICARE-MEDICAID")) {
      return "MMP";
    } else if (planType.toUpperCase().includes("1876 COST")) {
      return "1876";
    } else if (planType.toUpperCase().includes("HMO/HMOPOS")) {
      return "HMO";
    } else if (planType.toUpperCase().includes("LOCAL PPO") || planType.toUpperCase().includes("REGIONAL PPO")) {
      return "PPO";
    } else {
      return planType;
    }
  }

  return (
    <MedicareFiltersContext.Provider
      value={{
        applyingServiceArea,
        counties,
        mappedPlanTypes,
        mappedStarRatings,
        networkFiltersLoading,
        onApplyPrimaryFiltersClick,
        onApplyServiceAreaClick,
        onClearNetworkFiltersClick,
        onClearPrimaryFiltersClick,
        parentOrganizations,
        primaryFiltersLoading,
        selectedCounties,
        selectedMembershipCount,
        selectedParentOrganizations,
        selectedPlanTypes,
        selectedSnpFilter,
        selectedSortType,
        selectedStarRatings,
        selectedState,
        setSelectedCounties,
        setSelectedMembershipCount,
        setSelectedParentOrganizations,
        setSelectedPlanTypes,
        setSelectedSnpFilter,
        setSelectedSortType,
        setSelectedStarRatings,
        setSelectedState,
      }}>
      {props.children}
    </MedicareFiltersContext.Provider>
  )
}

export default MedicareFiltersContextProvider;